'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { Toast } from '@/app/components/overlays/toast';
import { DealerGoogleMaps } from '@/app/components/shared/dealer/components/shared/google-maps';
import { SearchPanel } from '@/app/components/shared/dealer/components/shared/search-panel';
import { useDealerSearchKey } from '@/app/components/shared/dealer/hooks/useDealerSearchKey';
import {
  DEALER_DEFAULT_VALUE,
  DEALER_SEARCH_KEY,
  SearchType,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import {
  findLocationsWithinRadius,
  searchDealersByKeyword,
} from '@/app/components/shared/dealer/utils/dealer-search';
import type {
  DealerLocation,
  DealerSearch,
} from '@/app/components/shared/dealer/utils/types';
import { useCurrentLocation } from '@/app/hooks/useCurrentLocation';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { cn } from '@/app/utils/cn';
import { clearForceLockScroll } from '@/app/utils/lock-scroll';

interface SearchResultProps {
  /**
   * list of dealers
   */
  dealers?: DealerSearch[];
  /**
   * selected dealer
   */
  selectedDealer?: DealerSearch | null;
}

export const DealerSearchResult = ({
  selectedDealer,
  dealers = [],
}: SearchResultProps) => {
  const searchParams = useSearchParams();
  const {
    getLocation,
    currentLocation,
    error: locationError,
  } = useCurrentLocation();
  const { allSearchKey, setSelectDealerKey, removeTextSearchParams } =
    useDealerSearchKey();

  const [searchValue, setSearchValue] = useState<string>(
    allSearchKey[DEALER_SEARCH_KEY.Q] ?? ''
  );

  const [selectedNewDealer, setSelectedNewDealer] = useState<
    DealerSearch | null | undefined
  >(selectedDealer ?? null);
  const [isLoadMapSuccess, setIsLoadMapSuccess] = useState<boolean>(false);

  const [dealerLocation, setDealerLocation] = useState<DealerLocation>({
    lat: 0,
    lng: 0,
  });
  const [searchType, setSearchType] = useState<SearchType>(SearchType.NATURAL);
  const [isFromMarker, setIsFromMarker] = useState<boolean>(false);
  const [hasHeader, setHasHeader] = useState<boolean>(true);

  const [isOpenToast, { open: openToast, close: closeToast }] =
    useDisclosure(false);

  useEffect(() => {
    clearForceLockScroll();
  }, []);

  const onSelectDealerFromMarker = (dealerId: string, tab?: string) => {
    const dealer = dealers.find((dealer) => dealer.uid === dealerId);

    if (!dealer) return;

    setSelectedNewDealer(dealer);
    setSelectDealerKey(dealer, tab);
    setIsFromMarker(true);
  };

  const boundLocation = useMemo(() => {
    try {
      const location = currentLocation
        ? { lat: currentLocation.lat, lng: currentLocation.lng }
        : undefined;

      return location;
    } catch (error) {
      return undefined;
    }
  }, [currentLocation]);

  useEffect(() => {
    if (dealerLocation.lat > 0 && dealerLocation.lng > 0) {
      setSearchType(SearchType.SUGGEST);
    } else if (currentLocation && !selectedNewDealer) {
      setSearchType(SearchType.CURRENT_LOCATION);
    } else {
      setSearchType(SearchType.NATURAL);
    }
  }, [currentLocation, dealerLocation, selectedNewDealer]);

  const findDealerBySuggestLocation = (dealerByKeyword: DealerSearch[]) => {
    const ONLY_KEYS = [
      DEALER_SEARCH_KEY.VehicleType,
      DEALER_SEARCH_KEY.Lat,
      DEALER_SEARCH_KEY.Lng,
      DEALER_SEARCH_KEY.Q,
    ];

    const keys = Array.from(searchParams?.keys?.() ?? []);
    const hasOtherKeys = keys.some(
      (key) => !ONLY_KEYS.includes(key as DEALER_SEARCH_KEY)
    );

    const scopeDealers = hasOtherKeys
      ? dealerByKeyword
      : searchDealersByKeyword(dealers, '', searchParams);

    if (scopeDealers.length === 0) {
      return [];
    }

    const dealerByLocation = findLocationsWithinRadius(
      scopeDealers,
      dealerLocation.lat,
      dealerLocation.lng
    );

    return dealerByLocation;
  };

  const handleCurrentLocation = () => {
    setSearchValue('');
    setSelectedNewDealer(null);
    setSearchType(SearchType.CURRENT_LOCATION);
  };

  const filterDealers = useMemo(() => {
    const dealerByKeyword = searchDealersByKeyword(
      dealers,
      boundLocation ? '' : searchValue,
      searchParams,
      boundLocation
    );

    if (
      dealerByKeyword.length === 0 &&
      dealerLocation.lat > 0 &&
      dealerLocation.lng > 0
    ) {
      return findDealerBySuggestLocation(dealerByKeyword);
    }

    return dealerByKeyword;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dealers,
    searchValue,
    searchParams,
    boundLocation,
    dealerLocation.lat,
    dealerLocation.lng,
  ]);

  // Get current location when jump from dealer home to dealer search
  useEffect(() => {
    const bound = localStorage.getItem(DEALER_SEARCH_KEY.Bound);
    if (bound) {
      getLocation();
      localStorage.removeItem(DEALER_SEARCH_KEY.Bound);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (locationError) {
      openToast();
    } else if (currentLocation) {
      handleCurrentLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation, locationError]);

  useEffect(() => {
    removeTextSearchParams(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleClearSearchType = () => {
    if (searchType !== SearchType.NATURAL) {
      setSearchType(SearchType.NATURAL);
    }
  };

  useEffect(() => {
    const header = document.querySelector('header[data-myi-hide]');
    if (header && getComputedStyle(header).display === 'none') {
      setHasHeader(false);
    }
  }, []);

  useEffect(() => {
    const lat = searchParams?.get(DEALER_SEARCH_KEY.Lat);
    const lng = searchParams?.get(DEALER_SEARCH_KEY.Lng);
    if (
      lat &&
      lng &&
      dealerLocation.lat === 0 &&
      dealerLocation.lng === 0 &&
      searchType !== SearchType.SUGGEST
    ) {
      setSearchType(SearchType.SUGGEST);
      setDealerLocation({
        lat: +lat || 0,
        lng: +lng || 0,
      });
    }
  }, [searchParams, dealerLocation, searchType]);

  return (
    <div
      className={cn('relative w-full overflow-hidden', {
        'h-screen-header': hasHeader,
        'h-screen': !hasHeader,
      })}
    >
      {!!isOpenToast && (
        <Toast
          type="error"
          title={DEALER_DEFAULT_VALUE.LOCATION_DENIED.title}
          description={DEALER_DEFAULT_VALUE.LOCATION_DENIED.description}
          onClose={closeToast}
        />
      )}
      <DealerGoogleMaps
        selectedDealer={selectedNewDealer}
        dealers={filterDealers}
        currentLocation={currentLocation}
        searchType={searchType}
        suggestPlaceLocation={dealerLocation}
        setIsLoadMapSuccess={setIsLoadMapSuccess}
        onSelectDealer={onSelectDealerFromMarker}
        onClickCurrentLocation={getLocation}
        isFromMarker={isFromMarker}
      />
      <SearchPanel
        className={cn('xl:center-y xl:absolute xl:left-4', {
          'opacity-0': !isLoadMapSuccess,
        })}
        dealers={filterDealers}
        selectedDealer={selectedNewDealer}
        searchValue={searchValue}
        searchType={searchType}
        setFromMarker={setIsFromMarker}
        setSearchValue={setSearchValue}
        setDealerLocation={setDealerLocation}
        setSelectDealer={setSelectedNewDealer}
        onClickCurrentLocation={getLocation}
        onClearSearchType={handleClearSearchType}
        setSearchType={setSearchType}
      />
    </div>
  );
};
